import openAIService from './openaiService';
import { getApiKey } from './authService';

/**
 * Service for handling image capture and processing
 */
class ImageService {
  /**
   * Process image with GPT Vision
   * @param imageFile Image file to process
   * @param prompt Prompt for GPT Vision
   * @param maxTokens Maximum tokens to generate (undefined for no limit)
   * @returns Promise resolving to generated text
   */
  async processImageWithGPT(
    imageFile: File,
    prompt: string,
    maxTokens?: number
  ): Promise<string> {
    try {
      // Get the API key from Firebase and set it
      const apiKey = await getApiKey();
      openAIService.setApiKey(apiKey);
      
      // Don't pass maxTokens if not specified to get complete response
      return await openAIService.getGPTVision(imageFile, prompt, maxTokens);
    } catch (error) {
      throw new Error('Failed to process the image. Please try again.');
    }
  }

  /**
   * Capture image from file input
   * @param fileInput File input element
   * @returns Promise resolving to File or null
   */
  async captureImage(fileInput: HTMLInputElement): Promise<File | null> {
    return new Promise((resolve) => {
      const handleChange = () => {
        if (fileInput.files && fileInput.files.length > 0) {
          resolve(fileInput.files[0]);
        } else {
          resolve(null);
        }
        fileInput.removeEventListener('change', handleChange);
      };

      fileInput.addEventListener('change', handleChange);
      fileInput.click();
    });
  }

  /**
   * Create a file input element for image capture
   * @param accept File types to accept (e.g., 'image/*')
   * @returns HTMLInputElement
   */
  createFileInput(accept: string = 'image/*'): HTMLInputElement {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = accept;
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    // Cleanup function to remove file input from DOM
    const cleanup = () => {
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput);
      }
    };

    // Clean up after user selects file
    fileInput.addEventListener('change', cleanup);

    // Clean up if user cancels (only works in some browsers)
    fileInput.addEventListener('cancel', cleanup);

    // Fallback: cleanup after 60 seconds to prevent memory leaks
    setTimeout(cleanup, 60000);

    return fileInput;
  }

  /**
   * Resize an image file
   * @param file Image file to resize
   * @param maxWidth Maximum width
   * @param maxHeight Maximum height
   * @param quality JPEG quality (0-1)
   * @returns Promise resolving to resized File
   */
  async resizeImage(
    file: File,
    maxWidth: number = 1024,
    maxHeight: number = 1024,
    quality: number = 0.8
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not create blob'));
              return;
            }
            
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            resolve(resizedFile);
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Error loading image'));
      };
    });
  }
}

export default new ImageService();
